import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Rocket, Check, Loader2, ArrowRight, FileEdit, Plus, Minus, RefreshCw, Database } from 'lucide-react';

const STAGE_META = {
  dev: { label: 'Development', color: 'text-amber-400 border-amber-500/40 bg-amber-500/10' },
  test: { label: 'Test', color: 'text-sky-400 border-sky-500/40 bg-sky-500/10' },
  production: { label: 'Production', color: 'text-emerald-400 border-emerald-500/40 bg-emerald-500/10' },
};

const CHANGE_ICON = {
  name: FileEdit, query: Database, visual_added: Plus, visual_removed: Minus, visual_modified: RefreshCw,
};

function fmt(ts) {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

export function PromotionDialog({ open, onOpenChange, reportId }) {
  const [status, setStatus] = useState(null);
  const [toStage, setToStage] = useState('test');
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [pending, setPending] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [promoted, setPromoted] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const st = await api.getPromotionStatus(reportId);
      setStatus(st);
      const d = await api.getPromotionDiff(reportId, toStage);
      setDiff(d);
      const ap = await api.listPromotionApprovals(reportId);
      const list = ap?.approvals || [];
      setPending(list.find(a => a.to_stage === toStage) || null);
    } catch (e) { toast.error('Failed to load promotion status'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open && reportId) load();   }, [open, reportId, toStage]);
  useEffect(() => { if (!open) setPromoted(null); }, [open]);

  const finishPromotion = (stage) => {
    try { sessionStorage.setItem('inzara:reports-dirty', '1'); } catch {}
    window.dispatchEvent(new CustomEvent('inzara:report-promoted', { detail: { reportId, stage } }));
    setPromoted(stage);
    setTimeout(() => onOpenChange(false), 1500);
  };

  const promote = async () => {
    setPromoting(true);
    try {
      await api.promoteReport(reportId, toStage);
      toast.success(`Promoted to ${STAGE_META[toStage].label}`);
      finishPromotion(toStage);
    } catch (e) { toast.error(e.message || 'Promotion failed'); }
    finally { setPromoting(false); }
  };

  const requestApproval = async () => {
    setRequesting(true);
    try {
      await api.requestPromotionApproval(reportId, toStage, '');
      toast.success(`Approval requested for ${STAGE_META[toStage].label}`);
      await load();
    } catch (e) { toast.error(e.message || 'Request failed'); }
    finally { setRequesting(false); }
  };

  const decide = async (decision) => {
    setRequesting(true);
    try {
      await api.decidePromotionApproval(pending.approval_id, decision);
      toast.success(decision === 'approve' ? `Approved & promoted to ${STAGE_META[toStage].label}` : 'Request rejected');
      if (decision === 'approve') {
        finishPromotion(toStage);
      } else {
        await load();
      }
    } catch (e) { toast.error(e.message || 'Action failed'); }
    finally { setRequesting(false); }
  };

  const fromStage = toStage === 'test' ? 'dev' : 'test';
  const changes = diff?.changes || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="promotion-dialog">
        {promoted ? (
          <div className="flex flex-col items-center justify-center text-center py-10 gap-4" data-testid="promotion-success">
            <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center animate-in zoom-in duration-300">
              <Check className="w-8 h-8 text-emerald-400" />
            </div>
            <div>
              <div className="text-base font-semibold text-foreground">Promoted to {STAGE_META[promoted].label}</div>
              <p className="text-xs text-muted-foreground mt-1">Your insight has been published successfully.</p>
            </div>
          </div>
        ) : (
        <>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><Rocket className="w-4 h-4 text-primary" /> Promote Insight</DialogTitle>
          <DialogDescription className="text-xs">Move this insight through Development → Test → Production. Viewers always see the Production version.</DialogDescription>
        </DialogHeader>

        {/* Stage pipeline */}
        <div className="flex items-center justify-between gap-2 py-2">
          {['dev', 'test', 'production'].map((s, i) => {
            const meta = STAGE_META[s];
            const info = s === 'dev' ? status?.stages?.dev : status?.stages?.[s];
            const exists = s === 'dev' ? true : info?.exists;
            return (
              <React.Fragment key={s}>
                <div className="flex flex-col items-center gap-1 flex-1">
                  <Badge variant="outline" className={`text-[10px] ${meta.color}`} data-testid={`stage-badge-${s}`}>{meta.label}</Badge>
                  <span className="text-[9px] text-muted-foreground text-center leading-tight">
                    {s === 'dev' ? `edited ${fmt(info?.updated_at)}` : exists ? `promoted ${fmt(info?.promoted_at)}` : 'not promoted'}
                  </span>
                </div>
                {i < 2 && <ArrowRight className="w-3 h-3 text-muted-foreground/70 flex-shrink-0 mb-3" />}
              </React.Fragment>
            );
          })}
        </div>

        {/* Target selector */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Promote to:</span>
          {['test', 'production'].map(s => (
            <button key={s} onClick={() => setToStage(s)} data-testid={`promote-target-${s}`}
              className={`px-2.5 py-1 rounded-md text-[11px] border transition-all ${toStage === s ? STAGE_META[s].color : 'border-border/40 text-muted-foreground hover:border-primary/40'}`}>
              {STAGE_META[s].label}
            </button>
          ))}
        </div>

        {/* Diff */}
        <div className="rounded-lg border border-border/40 bg-muted/10 p-3 max-h-52 overflow-auto" data-testid="promotion-diff">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">
            Changes from {STAGE_META[fromStage].label} → {STAGE_META[toStage].label}
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
          ) : !diff?.target_exists ? (
            <div className="text-xs text-emerald-400">First promotion — all content will be published to {STAGE_META[toStage].label}.</div>
          ) : changes.length === 0 ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground"><Check className="w-3 h-3 text-emerald-400" /> No changes — {STAGE_META[toStage].label} is already up to date.</div>
          ) : (
            <ul className="space-y-1">
              {changes.map((c, i) => {
                const Icon = CHANGE_ICON[c.type] || FileEdit;
                return (
                  <li key={i} className="flex items-center gap-2 text-[11px] text-foreground" data-testid={`diff-change-${i}`}>
                    <Icon className="w-3 h-3 text-primary flex-shrink-0" />
                    {c.label}{c.type === 'name' ? `: "${c.from || '—'}" → "${c.to || '—'}"` : ''}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} className="text-xs">Cancel</Button>
          {pending ? (
            <div className="flex items-center gap-2" data-testid="approval-pending">
              <Badge variant="outline" className="text-[10px] text-amber-400 border-amber-500/40 bg-amber-500/10">Approval requested by {pending.requested_by}</Badge>
              <Button size="sm" variant="outline" onClick={() => decide('reject')} disabled={requesting} className="text-xs border-rose-500/40 text-rose-400 hover:bg-rose-500/10" data-testid="reject-approval-btn">Reject</Button>
              <Button size="sm" onClick={() => decide('approve')} disabled={requesting} className="text-xs gap-1.5 bg-emerald-600 hover:bg-emerald-600/90" data-testid="approve-promote-btn">
                {requesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Approve &amp; Promote
              </Button>
            </div>
          ) : (
            <>
              <Button size="sm" variant="outline" onClick={requestApproval} disabled={requesting || loading || (diff?.target_exists && changes.length === 0)} className="text-xs gap-1.5" data-testid="request-approval-btn">
                {requesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Request approval
              </Button>
              <Button size="sm" onClick={promote} disabled={promoting || loading || (diff?.target_exists && changes.length === 0)} className="text-xs gap-1.5" data-testid="confirm-promote-btn">
                {promoting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5" />}
                Promote to {STAGE_META[toStage].label}
              </Button>
            </>
          )}
        </div>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}
